import React, { useMemo, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import ProgressBar from "../utils/ProgressBar";
import { generateRandomNumber } from "../utils/RandomNumberGenerator";
import MachineDisplayTree from "./MachineDisplayTree";
import { getWorstPartProgress } from "../utils/CounterFormat";

/* ---------- layout ---------- */

const MachineDisplayContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: #101317;
  color: #f5f5f5;
`;

const TopDock = styled.div`
  position: relative;
  z-index: 5;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: #151923;
`;

const AssemblyStrip = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 10px;
  }
`;

const AssemblyCard = styled.button`
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.35rem;
  width: 30rem;
  padding: 0.55rem 0.6rem;
  opacity: 70%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: linear-gradient(
    180deg,
    rgba(40, 90, 255, 0.20),
    rgba(40, 90, 255, 0.55)
  );
  color: inherit;
  cursor: pointer;
  outline: none;
  text-align: center;

  ${({ $active }) =>
    $active &&
    `
      opacity: 100%;
      border-color: rgba(76, 194, 255, 0.9);
      box-shadow: 0 0 0 2px rgba(76, 194, 255, 0.25);
      background: linear-gradient(
        180deg,
        rgba(76, 194, 255, 0.20),
        rgba(40, 90, 255, 0.65)
      );
    `}
`;

const AssemblyTopLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
`;

const AssemblyArticle = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  opacity: 0.95;
`;

const AssemblyWTag = styled.div`
  font-size: 1.05rem;
  opacity: 0.75;
  padding: 0.12rem 0.4rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(0, 0, 0, 0.25);
`;

const AssemblyDescription = styled.div`
  font-size: 1.12rem;
  opacity: 0.75;
  line-height: 1.15;
`;

function MachineDisplay({ machineList, selectedWNumberId }) {
  /**
   * Flatten to assemblies:
   * machineList: [{ wNumber, assemblies:[{id, articleNumber, description, parts...}, ...] }, ...]
   */
  const assemblies = useMemo(() => {
    if (!Array.isArray(machineList)) return [];

    const flattened = [];
    for (const w of machineList) {
      const wNumber = w.wNumber || w.WNumber || w.id || "";
      const list = Array.isArray(w.assemblies) ? w.assemblies : [];

      for (const a of list) {
        flattened.push({
          // stable identity first
          id: a.id || `${a.articleNumber || "?"}-${wNumber || "?"}`,
          wNumber,
          articleNumber: a.articleNumber || "",
          description: a.description || "",
          parts: Array.isArray(a.parts) ? a.parts : [],
          // keep anything else you want later
          endOfLife: a.endOfLife ?? null,
          primaryCounter: a.primaryCounter ?? null,
          usageCounters: a.usageCounters ?? [],
          temperatureZone: a.temperatureZone ?? null,
        });
      }
    }

    // Deduplicate (in case server repeats same assembly across wrappers)
    const map = new Map();
    for (const a of flattened) {
      const key = a.id || `${a.articleNumber}-${a.wNumber}`;
      if (!map.has(key)) map.set(key, a);
    }

    return Array.from(map.values());
  }, [machineList]);

  const [orderedAssemblies, setOrderedAssemblies] = useState([]);
  const [activeAssemblyId, setActiveAssemblyId] = useState(null);
  const [isDropOpen, setIsDropOpen] = useState(true);

  useEffect(() => {
    if (!assemblies.length) {
      setOrderedAssemblies([]);
      setActiveAssemblyId(null);
      setIsDropOpen(false);
      return;
    }

    // Sorting will have to be by largest progresbar when this is known:
    const sorted = [...assemblies].sort((a, b) =>
      (a.wNumber || "").localeCompare(b.wNumber || "") ||
      (a.articleNumber || "").localeCompare(b.articleNumber || "")
    );

    setOrderedAssemblies(sorted);
    setActiveAssemblyId(sorted[0].id);
    setIsDropOpen(true);
  }, [assemblies]);

  // external selection (POS click sends W number)
  useEffect(() => {
    if (!selectedWNumberId) return;

    setOrderedAssemblies((prev) => {
      const list = [...prev];
      const idx = list.findIndex((x) => x.wNumber === selectedWNumberId);
      if (idx === -1) return prev;

      const [picked] = list.splice(idx, 1);
      list.unshift(picked);
      return list;
    });

    const firstMatch = assemblies.find((a) => a.wNumber === selectedWNumberId);
    if (firstMatch) {
      setActiveAssemblyId(firstMatch.id);
      setIsDropOpen(true);
    }
  }, [selectedWNumberId, assemblies]);

  const handleSelectAssembly = useCallback(
    (id) => {
      setIsDropOpen((open) => (activeAssemblyId === id ? !open : true));
      setActiveAssemblyId(id);
    },
    [activeAssemblyId]
  );

  const activeAssembly =
    orderedAssemblies.find((a) => a.id === activeAssemblyId) || null;

  return (
    <MachineDisplayContainer>
      <TopDock>
        <AssemblyStrip>
          {orderedAssemblies.map((a) => (
            <AssemblyCard
              key={a.id}
              $active={a.id === activeAssemblyId}
              onClick={() => handleSelectAssembly(a.id)}
              aria-expanded={a.id === activeAssemblyId ? isDropOpen : false}
            >
              <AssemblyTopLine>
                {/* <AssemblyArticle>{a.articleNumber}</AssemblyArticle> */}
                {a.wNumber ? <AssemblyWTag>{a.wNumber}</AssemblyWTag> : null}
                {<ProgressBar progress={getWorstPartProgress(a.parts)}/>}
              </AssemblyTopLine>
              <AssemblyDescription>{a.description}</AssemblyDescription>
            </AssemblyCard>
          ))}
        </AssemblyStrip>

        {activeAssembly && isDropOpen && (
          <MachineDisplayTree activeAssembly={activeAssembly} />
        )}
      </TopDock>

    </MachineDisplayContainer>
  );
}

export default MachineDisplay;
